export type DropdownFieldLike = {
  type?: string;
  options?: string[];
};

export function isChecklistTruthyScalar(value: string | undefined | null): boolean {
  const text = String(value ?? "").trim().toLowerCase();
  return text === "true" || text === "1" || text === "yes" || text === "y";
}

export function isChecklistFalsyScalar(value: string | undefined | null): boolean {
  const text = String(value ?? "").trim().toLowerCase();
  return text === "false" || text === "0" || text === "no" || text === "n";
}

function normalizeTagHint(tag: string | undefined): string {
  return String(tag ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

function optionMatchesYes(opt: string): boolean {
  return /^(yes|y|true|1)$/i.test(String(opt).trim());
}

function optionMatchesNo(opt: string): boolean {
  return /^(no|n|false|0)$/i.test(String(opt).trim());
}

function isNegativeDropdownOption(opt: string): boolean {
  const lower = String(opt).trim().toLowerCase();
  return (
    optionMatchesNo(opt) ||
    lower === "deselect" ||
    lower === "none" ||
    lower === "n/a" ||
    lower.includes("individual") ||
    lower.includes("not applicable")
  );
}

function dropdownOptions(field: DropdownFieldLike): string[] {
  return Array.isArray(field.options)
    ? field.options.map((o) => String(o).trim()).filter(Boolean)
    : [];
}

function normalizeDropdownToken(value: string): string {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

function dropdownOptionForRegUnregToken(
  options: string[],
  token: string
): string | undefined {
  const norm = normalizeDropdownToken(token);
  if (!norm) return undefined;
  if (norm === "regd" || norm === "registered" || norm === "reg") {
    return options.find((opt) => {
      const o = normalizeDropdownToken(opt);
      return o === "regd" || o.startsWith("regd");
    });
  }
  if (norm === "unregd" || norm === "unregistered" || norm === "unreg") {
    return options.find((opt) => {
      const o = normalizeDropdownToken(opt);
      return o === "unregd" || o.startsWith("unregd");
    });
  }
  return undefined;
}

/**
 * Map scrutiny/document scalar values onto a checklist dropdown option string.
 * Handles case differences, yes/no options, and boolean document fields mapped to dropdowns.
 */
export function normalizeMappedDropdownValue(
  field: DropdownFieldLike,
  raw: string | undefined | null,
  tagHint?: string
): string {
  const val = String(raw ?? "").trim();
  if (!val) return "";
  const options = dropdownOptions(field);
  if (!options.length) return val;

  const exact = options.find((opt) => opt === val);
  if (exact) return exact;

  const lower = val.toLowerCase();
  const caseInsensitive = options.find((opt) => opt.toLowerCase() === lower);
  if (caseInsensitive) return caseInsensitive;

  const valNorm = normalizeDropdownToken(val);
  const byNorm = options.find((opt) => normalizeDropdownToken(opt) === valNorm);
  if (byNorm) return byNorm;

  const regUnreg = dropdownOptionForRegUnregToken(options, val);
  if (regUnreg) return regUnreg;

  const tagNorm = normalizeTagHint(tagHint);
  if (tagNorm) {
    const byTag = options.find(
      (opt) => opt.toLowerCase().replace(/[^a-z0-9]/g, "") === tagNorm
    );
    if (byTag) return byTag;
    if (isChecklistTruthyScalar(val)) {
      const byTagContains = options.find((opt) => opt.toLowerCase().includes(tagNorm));
      if (byTagContains) return byTagContains;
    }
  }

  if (isChecklistTruthyScalar(val)) {
    const yesOpt = options.find(optionMatchesYes);
    if (yesOpt) return yesOpt;
  }
  if (isChecklistFalsyScalar(val)) {
    const noOpt = options.find(optionMatchesNo);
    if (noOpt) return noOpt;
  }

  if (isChecklistTruthyScalar(val) || isChecklistFalsyScalar(val)) {
    const negativeOpts = options.filter(isNegativeDropdownOption);
    const positiveOpts = options.filter((o) => !isNegativeDropdownOption(o));
    if (isChecklistTruthyScalar(val)) {
      if (positiveOpts.length === 1) return positiveOpts[0];
      if (positiveOpts.length) return positiveOpts[0];
    }
    if (isChecklistFalsyScalar(val)) {
      if (negativeOpts.length === 1) return negativeOpts[0];
      if (negativeOpts.length) return negativeOpts[0];
    }
  }

  const partial = options.find(
    (opt) =>
      opt.toLowerCase().startsWith(lower) ||
      lower.startsWith(opt.toLowerCase()) ||
      opt.toLowerCase().includes(lower) ||
      lower.includes(opt.toLowerCase()) ||
      normalizeDropdownToken(opt).startsWith(valNorm) ||
      valNorm.startsWith(normalizeDropdownToken(opt))
  );
  if (partial) return partial;

  return val;
}

/** Resolve dropdown value for follow-up branch matching (`whenOption` must match an option). */
export function dropdownFollowUpBranchKey(
  field: DropdownFieldLike,
  value: string | undefined | null,
  tagHint?: string
): string {
  if (field.type !== "dropdown") return String(value ?? "");
  return normalizeMappedDropdownValue(field, value, tagHint);
}
